import { KeyboardEvent, useState } from 'react';
import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  Icon,
  Text,
} from '@equip.health/ui';
import { FOLLOW_UP_VISIBLE_CHIPS } from '../constants';

const BEST_MATCHES_KEY = '__best_matches__';

interface FollowUpChipsProps {
  id: string;
  series: FollowUpSeries[];
  activeSeriesKey: string | null;
  onSelect: (series: FollowUpSeries) => void;
  onBestMatchSelect: () => void;
  onClear: () => void;
}

interface ChipButtonProps {
  id: string;
  isActive: boolean;
  leftIcon?: FAIconName;
  label: string;
  onClick: () => void;
}

const ChipButton = ({ id, isActive, leftIcon, label, onClick }: ChipButtonProps) => {
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <ChakraFlex
      alignItems="center"
      aria-pressed={isActive}
      as="button"
      backgroundColor={isActive ? 'primary.100' : 'neutral.background.secondary'}
      borderColor="primary.100"
      borderRadius="100px"
      borderWidth="1px"
      cursor="pointer"
      gap="6px"
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      paddingX="12px"
      paddingY="6px"
      type="button"
    >
      {leftIcon && (
        <Icon
          color={isActive ? 'white' : 'primary.100'}
          name={leftIcon}
        />
      )}
      <Text
        color={isActive ? 'white' : 'primary.100'}
        id={`${id}__label`}
        variant="captionMedium"
      >
        {label}
      </Text>
    </ChakraFlex>
  );
};

const FollowUpChips = ({
  id,
  series,
  activeSeriesKey,
  onSelect,
  onBestMatchSelect,
  onClear,
}: FollowUpChipsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!series.length) return null;

  const hasOverflow = series.length > FOLLOW_UP_VISIBLE_CHIPS;
  const visibleSeries =
    hasOverflow && !isExpanded
      ? series.slice(0, FOLLOW_UP_VISIBLE_CHIPS)
      : series;

  const isBestMatchActive = activeSeriesKey === BEST_MATCHES_KEY;

  return (
    <ChakraFlex
      backgroundColor={'neutral.background.secondary'}
      borderRadius="16px"
      direction="column"
      gap="12px"
      padding="12px 16px"
    >
      <Text color="neutral.primary" id={`${id}__chips-label`} variant="captionMedium">
        Auto-fill time slots based on repeating availability and patient
        preferences.
      </Text>
      <ChakraFlex alignItems="center" flexWrap="wrap" gap="12px">
        <ChipButton
          id={`${id}__chip-best-matches`}
          isActive={isBestMatchActive}
          label="Best matches"
          leftIcon={FAIconName.starRegular}
          onClick={() =>
            isBestMatchActive ? onClear() : onBestMatchSelect()
          }
        />
        {visibleSeries.map((s) => {
          const isActive = s.key === activeSeriesKey;
          return (
            <ChipButton
              id={`${id}__chip-${s.key}`}
              isActive={isActive}
              key={s.key}
              label={s.chipLabel}
              leftIcon={FAIconName.arrowsRotateRegular}
              onClick={() => (isActive ? onClear() : onSelect(s))}
            />
          );
        })}
        {hasOverflow ? (
          <FireflyButton
            id={`${id}__chips-see-more`}
            onClick={() => setIsExpanded((prev) => !prev)}
            variant="tertiary"
          >
            {isExpanded ? 'See less' : 'See more'}
          </FireflyButton>
        ) : null}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export { BEST_MATCHES_KEY };
export default FollowUpChips;
